import * as React from "react";
import { CheckCircle2, FileText, Lock, Sparkles } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TopicCoverage, TopicParticipant } from "@/lib/api";

/** First letters of the first two words, for an avatar fallback. */
function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Overlapping avatar stack + count for the people currently in a live topic. */
function Participants({ people }: { people: TopicParticipant[] }) {
  const shown = people.slice(0, 4);
  const extra = people.length - shown.length;
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-1.5">
        {shown.map((p) => (
          <span
            key={p.identity}
            title={p.name ?? p.identity}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-surface bg-elevated text-[10px] font-medium text-text"
          >
            {initials(p.name ?? p.identity)}
          </span>
        ))}
        {extra > 0 ? (
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-surface bg-elevated text-[10px] font-medium text-muted">
            +{extra}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-muted">
        {people.length === 1
          ? (people[0].name ?? "1 person")
          : `${people.length} in the room`}
      </span>
    </div>
  );
}

/** Flatten markdown to plain text for the clamped one/two-line card preview. */
function toPreview(text: string): string {
  return text
    .replace(/`{1,3}([^`]*)`{1,3}/g, "$1") // code spans
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/^#{1,6}\s*/gm, "") // headings
    .replace(/^\s*[-*+]\s+/gm, "") // bullet markers
    .replace(/\s+/g, " ")
    .trim();
}

/** Thin bar showing how many key points have been covered so far. */
function CoverageBar({ coverage }: { coverage: TopicCoverage }) {
  if (coverage.total === 0) return null;
  const pct = Math.round((coverage.covered / coverage.total) * 100);
  const full = coverage.covered >= coverage.total;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] text-muted">
        <span>Coverage</span>
        <span className={cn(full && "text-emerald-600 dark:text-emerald-400")}>
          {coverage.covered}/{coverage.total}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-elevated">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            full
              ? "bg-emerald-500"
              : coverage.covered > 0
                ? "bg-amber-500"
                : "bg-muted/40",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** "AI suggests…" chip + no-op Accept button, shown when the AI has a resolution proposal. */
function AiSuggests({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-brand-600/20 bg-brand-600/5 px-3 py-2">
      <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-brand-600" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-600">
          AI suggests
        </p>
        <p className="line-clamp-2 text-xs text-muted">{text}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-6 shrink-0 px-2 text-[10px]"
        onClick={(e) => e.stopPropagation()}
      >
        Accept
      </Button>
    </div>
  );
}

/**
 * Presentational card for a single meeting topic. The dashboard maps a `GET /api/topics`
 * topic onto these props (title, status badge, brief summary, stage action).
 */
export interface TopicCardProps {
  title: string;
  /** Brief AI summary, clamped to two lines and shown muted under the title. */
  description?: string;
  /** Marks `description` as a seed-derived provisional blurb (vs a real discussion summary). */
  provisional?: boolean;
  /** Status pill. */
  badge?: { label: string; variant?: BadgeProps["variant"]; className?: string };
  /** People currently in the room (shown as an avatar stack on active topics). */
  participants?: TopicParticipant[];
  /** Coverage of key points for this topic. */
  coverage?: TopicCoverage;
  /** Output templates attached to the topic — shown as small "deliverable" tags. */
  templates?: { slug: string; name: string }[];
  /** Department the topic belongs to — shown as a small chip. */
  department?: string | null;
  /** AI-proposed resolution text, shown as a chip with an Accept button. */
  aiSuggestedResolution?: string | null;
  /** Shows a lock badge next to the title when true (topic restricted to its members). */
  isPrivate?: boolean;
  /** Primary or quiet action (e.g. a Join / Start button). */
  action?: React.ReactNode;
  /** Selecting the card (click / Enter / Space) opens the topic detail. */
  onSelect?: () => void;
}

export function TopicCard({
  title,
  description,
  provisional,
  badge,
  participants,
  coverage,
  templates,
  department,
  aiSuggestedResolution,
  isPrivate,
  action,
  onSelect,
}: TopicCardProps) {
  return (
    <Card
      onClick={onSelect}
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              // Only the card itself — not a focused action button inside it.
              if (
                e.target === e.currentTarget &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      className={cn(
        onSelect &&
          "cursor-pointer transition-colors hover:border-brand-600/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
      )}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex min-w-0 items-center gap-1.5 text-sm">
            {isPrivate ? (
              <Lock
                className="size-3.5 shrink-0 text-muted"
                aria-label="Private topic"
              />
            ) : null}
            <span className="truncate">{title}</span>
          </CardTitle>
          {badge ? (
            <Badge variant={badge.variant} className={badge.className}>
              {badge.label}
            </Badge>
          ) : null}
        </div>
        {description ? (
          <div className="space-y-1">
            {provisional ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted">
                <Sparkles className="size-3" />
                AI · provisional
              </span>
            ) : null}
            <p className="line-clamp-2 text-sm leading-relaxed text-muted">
              {toPreview(description)}
            </p>
          </div>
        ) : null}
      </CardHeader>
      {(participants?.length ||
        (coverage && coverage.total > 0) ||
        templates?.length ||
        department ||
        aiSuggestedResolution ||
        action) ? (
        <CardContent className="space-y-3">
          {department ? (
            <span className="inline-flex w-fit items-center rounded-full bg-brand-600/10 px-2 py-0.5 text-[10px] font-medium text-brand-600">
              {department}
            </span>
          ) : null}
          {templates?.length ? (
            <div className="flex flex-wrap gap-1">
              {templates.map((t) => (
                <span
                  key={t.slug}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-elevated px-2 py-0.5 text-[10px] text-muted"
                >
                  <FileText className="size-2.5" />
                  {t.name}
                </span>
              ))}
            </div>
          ) : null}
          {participants?.length ? <Participants people={participants} /> : null}
          {coverage && coverage.total > 0 ? (
            <CoverageBar coverage={coverage} />
          ) : null}
          {aiSuggestedResolution ? (
            <AiSuggests text={aiSuggestedResolution} />
          ) : null}
          {action ? (
            <div onClick={(e) => e.stopPropagation()}>{action}</div>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}
